import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, setToken } from './api';

export interface AdminUser {
  id: string;
  phone: string;
  name: string | null;
  role: 'business' | 'courier' | 'both' | 'admin';
}

interface Ctx {
  user: AdminUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const C = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (): Promise<void> => {
    try {
      const me = await api<AdminUser>('/api/auth/me');
      setUser(me);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try { await api('/api/auth/logout', { method: 'POST' }); } catch {}
    setToken(null);
    setUser(null);
  };

  useEffect(() => { refresh(); }, []);

  return <C.Provider value={{ user, loading, refresh, logout }}>{children}</C.Provider>;
}

export function useAuth(): Ctx {
  const v = useContext(C);
  if (!v) throw new Error('useAuth needs AuthProvider');
  return v;
}
